// utils/TokenService.js
// Phase 1: Token service with backward compatibility for localStorage

let memoryAccessToken = null;
let authTransitionRevision = 0;
const USE_LEGACY_STORAGE = process.env.REACT_APP_AUTH_PERSIST_LOCALSTORAGE !== 'false';

export const AUTH_TRANSITION_SUPERSEDED = 'AUTH_TRANSITION_SUPERSEDED';
export const AUTH_IDENTITY_MISMATCH = 'AUTH_IDENTITY_MISMATCH';

export const createSupersededAuthError = () => {
  const error = new Error('Authentication operation was superseded by a newer login or logout');
  error.code = AUTH_TRANSITION_SUPERSEDED;
  return error;
};

export const createAuthIdentityMismatchError = () => {
  const error = new Error('Refreshed session belongs to a different user');
  error.code = AUTH_IDENTITY_MISMATCH;
  return error;
};

export const isSameUserIdentity = (left, right) => {
  const leftId = left?.id ?? left?.userId ?? left?.user_id;
  const rightId = right?.id ?? right?.userId ?? right?.user_id;

  return leftId != null && rightId != null && String(leftId) === String(rightId);
};

/**
 * TokenService - Manages access token storage with backward compatibility
 * Phase 1: Supports both memory and localStorage (legacy)
 * Future phases will remove localStorage dependency
 */
export const TokenService = {
  /**
   * Start an explicit authentication transition. Async refreshes capture this
   * revision and must not commit after a newer login or logout begins.
   */
  beginAuthTransition() {
    authTransitionRevision += 1;
    return authTransitionRevision;
  },

  getAuthTransitionRevision() {
    return authTransitionRevision;
  },

  isAuthTransitionCurrent(revision) {
    return revision === authTransitionRevision;
  },

  /**
   * Set access token in memory and optionally localStorage (for backward compatibility)
   * @param {string|null} token - The access token to store
   */
  setAccessToken(token) {
    memoryAccessToken = token || null;

    if (USE_LEGACY_STORAGE) {
      if (token) {
        localStorage.setItem('token', token);
      } else {
        localStorage.removeItem('token');
      }
    }
  },

  /**
   * Get access token from memory (preferred) or localStorage (legacy fallback)
   * @returns {string|null} The access token or null
   */
  getAccessToken() {
    return memoryAccessToken || (USE_LEGACY_STORAGE && localStorage.getItem('token')) || null;
  },

  /**
   * Clear access token from both memory and localStorage
   */
  clearAccessToken() {
    // Invalidate refresh responses that were started for the previous user.
    authTransitionRevision += 1;
    memoryAccessToken = null;

    if (USE_LEGACY_STORAGE) {
      localStorage.removeItem('token');
    }
  },
};

export default TokenService;
